import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, MapPin, ArrowLeft, ThumbsUp, CheckCircle, ChevronRight } from 'lucide-react';
import { useData } from '@happyspace/sdk/data';
import CategoryNav from './CategoryNav';

// ── Types ──────────────────────────────────────────────────────────────────
interface UserReview {
  _id?: string;
  id?: string;
  name: string;
  initials: string;
  color: string;
  date: string;
  rating: number;
  reviewText: string;
  helpfulCount: number;
  slug: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────
const AVATAR_COLORS = ['#f97316', '#2563eb', '#16a34a', '#7c3aed', '#db2777', '#0891b2'];

function getInitials(name: string) {
  return name.trim().split(/\s+/).map((w) => w[0]?.toUpperCase() ?? '').slice(0, 2).join('');
}

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatNow() {
  return new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// ── Static restaurant data ─────────────────────────────────────────────────
const RESTAURANT_DATA: Record<string, {
  name: string;
  location: string;
  topRated: boolean;
  image: string;
  category: string;
}> = {
  'spice-rack': {
    name: 'Spice Rack',
    location: 'Frisco',
    topRated: true,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783461078615-6lc4w.png',
    category: 'Indian Restaurant',
  },
  'house-of-biryanis-and-kebabs': {
    name: 'House of Biryanis and Kebabs',
    location: 'Frisco',
    topRated: false,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782093325592-95c18t.jpg',
    category: 'Indian Restaurant',
  },
  'dil-se-desi': {
    name: 'Dil se Desi',
    location: 'Plano',
    topRated: false,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782092242877-c8dul.jpg',
    category: 'Indian Restaurant',
  },
  'hyderabad-house': {
    name: 'Hyderabad House',
    location: 'Irving',
    topRated: false,
    image: 'https://images.unsplash.com/photo-1606471191009-63994c53433b?w=1200&h=500&fit=crop',
    category: 'Indian Restaurant',
  },
  'the-monks': {
    name: 'The Monks',
    location: 'Lewisville',
    topRated: false,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782090561864-13rclj.jpg',
    category: 'Indian Restaurant',
  },
  'simply-south': {
    name: 'Simply South',
    location: 'McKinney',
    topRated: false,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782091089501-g0rbai.jpg',
    category: 'Indian Restaurant',
  },
};

// ── Sub-components ─────────────────────────────────────────────────────────
function StarDisplay({ value, size = 'sm' }: { value: number; size?: 'sm' | 'lg' }) {
  const px = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={px}
          fill={value >= s ? '#f97316' : value >= s - 0.5 ? '#fdba74' : 'none'}
          stroke={value >= s ? '#f97316' : '#d1d5db'}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          onMouseEnter={() => setHovered(s)}
          onMouseLeave={() => setHovered(0)}
          className="transition-transform hover:scale-110 focus:outline-none"
        >
          <Star
            className="w-7 h-7"
            fill={(hovered || value) >= s ? '#f97316' : 'none'}
            stroke={(hovered || value) >= s ? '#f97316' : '#d1d5db'}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function RestaurantReview() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const restaurant = RESTAURANT_DATA[slug ?? ''];

  // Per-restaurant user reviews — persisted via HappySpace Data SDK
  const { rows: sdkReviews, loading: reviewsLoading, insert, refetch } = useData('restaurant_reviews', {
    autoFetch: true,
    where: slug ? { slug } : {},
    orderBy: { createdAt: -1 },
  });
  const [optimisticReview, setOptimisticReview] = useState<UserReview | null>(null);
  const [newReviewId, setNewReviewId] = useState<string | null>(null);
  const newReviewRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState({ name: '', rating: 0, reviewText: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [helpful, setHelpful] = useState<Record<string, boolean>>({});

  // Merge SDK rows with optimistic review, deduplicate
  const userReviews: UserReview[] = (() => {
    const sdkMapped: UserReview[] = (sdkReviews || []).map((r: any) => ({
      ...r,
      id: r._id || r.id,
      _id: r._id,
    }));
    if (optimisticReview && !sdkMapped.some((r) => r._id === optimisticReview._id)) {
      return [optimisticReview, ...sdkMapped];
    }
    return sdkMapped;
  })();

  // Scroll newly submitted review into view
  useEffect(() => {
    if (newReviewId && newReviewRef.current) {
      setTimeout(() => {
        newReviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, [newReviewId]);

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <p className="text-gray-500 text-lg">Restaurant not found.</p>
        <button onClick={() => navigate('/restaurants')} className="mt-4 text-orange-500 font-semibold hover:underline">
          ← Back to Restaurants
        </button>
      </div>
    );
  }

  // ── Rating calculation (real user-submitted reviews only) ────────────────
  const totalReviews = userReviews.length;
  const userWeightedSum = userReviews.reduce((sum, r) => sum + r.rating, 0);
  const avgRating = totalReviews > 0
    ? Math.round((userWeightedSum / totalReviews) * 10) / 10
    : 0;

  // Distribution [5★, 4★, 3★, 2★, 1★]
  const dist = [5, 4, 3, 2, 1].map((star) =>
    userReviews.filter((r) => r.rating === star).length
  );
  const pct = (count: number) => totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
  const barColors = ['#16a34a', '#22c55e', '#f97316', '#fb923c', '#ef4444'];

  // ── Form handlers ───────────────────────────────────────────────────────
  function validate() {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.rating) e.rating = 'Please select a rating.';
    if (!form.reviewText.trim()) e.reviewText = 'Please write a review.';
    return e;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});

    const reviewId = `user-${Date.now()}`;
    const newReview: UserReview = {
      _id: reviewId,
      id: reviewId,
      name: form.name.trim(),
      initials: getInitials(form.name),
      color: getAvatarColor(form.name),
      date: formatNow(),
      rating: form.rating,
      reviewText: form.reviewText.trim(),
      helpfulCount: 0,
      slug: slug!,
    };

    // Show optimistically right away
    setOptimisticReview(newReview);
    setNewReviewId(reviewId);
    setSubmitted(true);
    setForm({ name: '', rating: 0, reviewText: '' });

    // Persist in background via SDK
    try {
      await insert({
        ...newReview,
        status: 'published',
        submittedAt: new Date().toISOString(),
      });
      await refetch();
    } catch (err) {
      console.error('Failed to persist review:', err);
    }
  }

  function handleChange(field: string, value: string | number) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f3f4f6]" style={{ fontFamily: "'Poppins', sans-serif" }}>

      {/* ── HERO ── */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '220px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url("${restaurant.image}")` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(8,47,109,0.72) 0%, rgba(8,47,109,0.55) 60%, rgba(8,47,109,0.82) 100%)' }} />

        <div className="relative z-10 max-w-[1320px] mx-auto px-4 pt-6 pb-10 flex flex-col items-center text-center">
          <motion.button
            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}
            onClick={() => navigate('/restaurants')}
            className="self-start flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium mb-5 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Restaurants
            <ChevronRight className="w-3 h-3 opacity-60" />
            <span className="text-white/60">{restaurant.name}</span>
          </motion.button>

          {restaurant.topRated && (
            <motion.span
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}
              className="inline-flex items-center gap-1 mb-2 text-[11px] font-semibold px-3 py-1 rounded-full"
              style={{ background: 'rgba(251,191,36,0.2)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.35)' }}
            >
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
              Top Rated by @tastebudsbyanubhi
            </motion.span>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[32px] font-extrabold leading-tight tracking-tight drop-shadow"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {restaurant.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center justify-center gap-3 mt-2"
          >
            <span className="flex items-center gap-1 text-white/80 text-[13px]">
              <MapPin className="w-3.5 h-3.5 text-orange-400" />
              {restaurant.location}, TX
            </span>
            <span className="text-white/40 hidden sm:inline">·</span>
            <span className="text-white/70 text-[13px]">Indian Restaurant</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.24 }}
            className="mt-3 inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5"
          >
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            {totalReviews > 0 ? (
              <>
                <span className="text-white font-bold text-[15px]">{avgRating}</span>
                <span className="text-white/60 text-[12px]">({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})</span>
              </>
            ) : (
              <span className="text-white/80 text-[13px]">No reviews yet</span>
            )}
          </motion.div>
        </div>

        <svg viewBox="0 0 1440 28" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, bottom: '-1px', width: '100%', height: '22px', display: 'block', zIndex: 3 }}>
          <path d="M0,0 Q720,28 1440,0 L1440,28 L0,28 Z" fill="#f3f4f6" />
        </svg>
      </section>

      <CategoryNav />

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-[1100px] mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-6 items-start">

          {/* ── LEFT COLUMN ── */}
          <div className="flex-1 min-w-0 space-y-5">

            {/* Rating Summary Card */}
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.1 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
            >
              <h2 className="text-[14px] font-bold text-gray-800 mb-4 uppercase tracking-wide">Rating Overview</h2>
              {totalReviews > 0 ? (
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                  <div className="flex flex-col items-center shrink-0">
                    <span className="text-[56px] font-extrabold text-gray-900 leading-none">{avgRating}</span>
                    <StarDisplay value={avgRating} size="lg" />
                    <span className="text-[12px] text-gray-400 mt-1">{totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}</span>
                  </div>
                  <div className="flex-1 w-full space-y-2">
                    {[5, 4, 3, 2, 1].map((star, i) => (
                      <div key={star} className="flex items-center gap-2">
                        <span className="text-[12px] text-gray-500 w-4 text-right shrink-0">{star}</span>
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400 shrink-0" />
                        <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                          <motion.div
                            key={`${star}-${dist[i]}`}
                            initial={{ width: 0 }}
                            animate={{ width: `${pct(dist[i])}%` }}
                            transition={{ duration: 0.6, delay: 0.2 + i * 0.06 }}
                            className="h-full rounded-full"
                            style={{ background: barColors[i] }}
                          />
                        </div>
                        <span className="text-[11px] text-gray-400 w-6 shrink-0">{pct(dist[i])}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-[13px] text-gray-500 py-2">No ratings yet. Be the first to rate {restaurant.name}.</p>
              )}
            </motion.div>

            {/* Reviews List */}
            <div>
              <h2 className="text-[14px] font-bold text-gray-700 uppercase tracking-wide mb-3">
                Community Reviews
                <span className="ml-2 text-[12px] font-normal text-gray-400 normal-case">({totalReviews})</span>
              </h2>

              <div className="space-y-4">
                {/* ── User-submitted reviews appear first ── */}
                <AnimatePresence initial={false}>
                  {userReviews.map((review) => (
                    <motion.div
                      key={review.id}
                      ref={review.id === newReviewId ? newReviewRef : undefined}
                      initial={{ opacity: 0, y: -20, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4, ease: 'easeOut' }}
                      className="bg-white rounded-2xl border p-5"
                      style={{
                        borderColor: review.id === newReviewId ? '#fed7aa' : '#f3f4f6',
                        boxShadow: review.id === newReviewId
                          ? '0 0 0 2px #f97316, 0 4px 16px rgba(249,115,22,0.1)'
                          : '0 1px 3px rgba(0,0,0,0.05)',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[13px] font-bold shrink-0"
                          style={{ background: review.color }}
                        >
                          {review.initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="text-[14px] font-semibold text-gray-900">{review.name}</span>
                            <span className="text-[11px] text-gray-400">{review.date}</span>
                          </div>
                          <StarDisplay value={review.rating} />
                          <p className="text-[13px] text-gray-600 leading-relaxed mt-2">{review.reviewText}</p>
                          <button
                            onClick={() => { const rk = (review._id || review.id || ''); setHelpful((prev) => ({ ...prev, [rk]: !prev[rk] })); }}
                            className={`mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1 rounded-full border transition-all ${
                              helpful[review._id || review.id || '']
                                ? 'bg-blue-50 border-blue-200 text-blue-600'
                                : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                            }`}
                          >
                            <ThumbsUp className="w-3.5 h-3.5" />
                            Helpful ({review.helpfulCount + (helpful[review._id || review.id || ''] ? 1 : 0)})
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* ── Empty state when no community reviews yet ── */}
                {userReviews.length === 0 && (
                  <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-8 text-center">
                    <p className="text-[14px] font-semibold text-gray-700">No reviews yet</p>
                    <p className="text-[12.5px] text-gray-500 mt-1">
                      Be the first to review {restaurant.name} — use the form to share your experience.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── RIGHT SIDEBAR ── */}
          <div className="w-full lg:w-[320px] shrink-0 lg:sticky lg:top-6">
            <motion.div
              initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="px-5 pt-5 pb-4 border-b border-gray-100">
                <h3 className="text-[15px] font-bold text-gray-900">Write a Review</h3>
                <p className="text-[12px] text-gray-500 mt-0.5">
                  Share your experience at <span className="font-semibold text-gray-700">{restaurant.name}</span>
                </p>
              </div>

              {/* Success banner — shown after submit, stays until dismissed */}
              <AnimatePresence>
                {submitted && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="mx-5 mt-4 flex items-start gap-2.5 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                      <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[12.5px] font-semibold text-green-800">Review added!</p>
                        <p className="text-[11.5px] text-green-700 mt-0.5 leading-snug">
                          Your review for <span className="font-semibold">{restaurant.name}</span> is now visible below.
                        </p>
                        <button
                          onClick={() => setSubmitted(false)}
                          className="mt-1.5 text-[11px] text-blue-500 hover:underline font-medium"
                        >
                          Write another review
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Form — always visible so another review can be written */}
              <form onSubmit={handleSubmit} noValidate className="p-5 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Your Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g. Priya Sharma"
                    className={`w-full border rounded-xl px-3.5 py-2.5 text-[13px] text-gray-800 outline-none transition-all focus:ring-2 focus:ring-orange-200 focus:border-orange-400 placeholder:text-gray-400 ${errors.name ? 'border-red-400' : 'border-gray-200'}`}
                  />
                  {errors.name && <p className="text-red-500 text-[11px] mt-1">{errors.name}</p>}
                </div>

                {/* Rating */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Rating <span className="text-red-400">*</span>
                  </label>
                  <StarPicker value={form.rating} onChange={(v) => handleChange('rating', v)} />
                  {errors.rating && <p className="text-red-500 text-[11px] mt-1">{errors.rating}</p>}
                </div>

                {/* Review text */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Your Review <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={form.reviewText}
                    onChange={(e) => handleChange('reviewText', e.target.value)}
                    placeholder="Tell us about your experience — food, service, ambiance..."
                    rows={4}
                    className={`w-full border rounded-xl px-3.5 py-2.5 text-[13px] text-gray-800 outline-none transition-all focus:ring-2 focus:ring-orange-200 focus:border-orange-400 placeholder:text-gray-400 resize-none ${errors.reviewText ? 'border-red-400' : 'border-gray-200'}`}
                  />
                  {errors.reviewText && <p className="text-red-500 text-[11px] mt-1">{errors.reviewText}</p>}
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white font-bold text-[13.5px] py-3 rounded-xl transition-colors"
                >
                  Submit Review
                </motion.button>

                <p className="text-[11px] text-gray-400 text-center">
                  Your review will be visible to the DallasDesiScoop community.
                </p>
              </form>
            </motion.div>

            {/* Restaurant mini card */}
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}
              className="mt-4 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <img src={restaurant.image} alt={restaurant.name} className="w-full h-[110px] object-cover" />
              <div className="p-4">
                <h4 className="text-[13.5px] font-bold text-gray-900">{restaurant.name}</h4>
                <div className="flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3 text-orange-500" />
                  <span className="text-[12px] text-gray-500">{restaurant.location}, TX</span>
                </div>
                {totalReviews > 0 ? (
                  <div className="flex items-center gap-1.5 mt-2">
                    <StarDisplay value={avgRating} />
                    <span className="text-[12px] font-semibold text-gray-700">{avgRating}</span>
                    <span className="text-[11px] text-gray-400">({totalReviews})</span>
                  </div>
                ) : (
                  <p className="text-[11px] text-gray-400 mt-2">No reviews yet</p>
                )}
                {restaurant.topRated && (
                  <span className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold text-amber-700">
                    <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                    Top Rated by @tastebudsbyanubhi
                  </span>
                )}
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </div>
  );
}
